import axios, { AxiosError, type AxiosResponse } from "axios";
import { cloneDeep, assign, merge } from "lodash";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { endpoints } from "@/endpoints";
import { tags, unwrapDeepRefs } from "@/api/common";
import useApiStore from "@/stores/api";
import useAuthStore from "@/stores/auth";
import { useLocationSelector } from "@/composables/location";

import type { DeepPartial, ValueOf } from "@/types/utility";
import {
  type CompanySettings,
  type UpdateCompanyApiRequest,
  type CompanySettingsApiRequest,
  type GetCompanyApiResponse,
  type CreateCompanyPayload,
  type CreateCompanyError,
  useGetCompanySettings,
} from "@/api/companies";
import { queryKeys } from "../queryKeys";

type SettingValue = string | number | boolean | null | undefined;
type UnknownSettings = {
  [key: string]:
    | SettingValue
    | UnknownSettings
    | (SettingValue | UnknownSettings)[];
};

const flattenSettingsKeys = (
  settings: UnknownSettings,
  prefix = ""
): { key: string; value: SettingValue | [] }[] => {
  const results: { key: string; value: SettingValue | [] }[] = [];

  Object.entries(settings).forEach(([key, value]) => {
    const newPrefix = (
      prefix ? `${prefix}.${key}` : key
    ) as keyof CompanySettings;

    // Setting is a primitive
    if (typeof value !== "object" || value === null) {
      results.push({ key: newPrefix, value });
      return;
    }

    // Setting is an object
    if (!Array.isArray(value)) {
      results.push(...flattenSettingsKeys(value, newPrefix));
      return;
    }

    // Setting is an array that is not empty
    if (value.length) {
      value.forEach((item, index) => {
        // Array value is an object
        if (typeof item === "object" && item !== null) {
          results.push(...flattenSettingsKeys(item, `${newPrefix}[${index}]`));
        } else {
          // Array value is a primitive
          results.push({ key: `${newPrefix}[${index}]`, value: item });
        }
      });
      return;
    }

    // Setting is an empty array
    results.push({ key: newPrefix, value: [] });
  });

  return results;
};

const updateSettingObject = (
  settings: UnknownSettings,
  path: string,
  value: SettingValue
) => {
  const keys = path.split(".");
  let current = settings;

  // Recursively traverse the object until we reach the end of the path
  keys.forEach((key, index) => {
    const isArrayElement = /\[\d+\]$/.test(key);
    const arrayIndexMatch = key.match(/\[(\d+)\]$/);

    // This part of the path is an array
    if (isArrayElement && arrayIndexMatch) {
      const parentKey = key.replace(/\[\d+\]$/, "");
      const arrayIndex = parseInt(arrayIndexMatch[1], 10);

      if (!current[parentKey]) {
        current[parentKey] = [];
      }

      if (index === keys.length - 1) {
        (current[parentKey] as SettingValue[])[arrayIndex] = value;
        return;
      }

      const parentArray = current[parentKey] as UnknownSettings[];
      if (!parentArray[arrayIndex]) {
        parentArray[arrayIndex] = {};
      }
      current = parentArray[arrayIndex] as UnknownSettings;
      return;
    }

    if (index === keys.length - 1) {
      current[key] = value;
      return;
    }

    if (!current[key]) {
      current[key] = {};
    }
    current = current[key] as UnknownSettings;
  });

  return settings;
};

const mapFromCompanySettings = (
  input: DeepPartial<CompanySettings>
): CompanySettingsApiRequest => {
  let settings: CompanySettingsApiRequest = {};

  flattenSettingsKeys(input).forEach(({ key, value }) => {
    const wildcardKey = key.replace(/\[\d+\]/g, "[*]");
    switch (wildcardKey) {
      // Numbers:
      case "print_label_number":
      case "host_selection_custom_field":
      case "visit_duration_notification_hours":
      case "auto_anonymise_days_visitor":
        settings = updateSettingObject(
          settings,
          key,
          (value as number) ?? null
        );
        break;

      // Booleans:
      case "employee_roaming":
      case "agreement_append":
      case "agreement_hide":
      case "visits_require_signature":
      case "visitor_enable_label_scan":
      case "hide_inactive_fields":
      case "host_selection_department_field":
      case "slack_host_notifications":
      case "slack_visitor_approval_notifications":
      case "employee_message_required":
      case "employee_remote":
      case "emp_autologout":
      case "profile_capture_e":
      case "photo_capture_e":
      case "employee_secure_mode":
      case "show_employee_in_out_timestamp":
      case "alert_notifications_enabled":
      case "email_global_cc":
      case "host_approval_notifications":
      case "host_notifications_show_fields":
      case "host_notifications":
      case "photo_capture_v":
      case "show_employee_list":
      case "show_out_state":
      case "visit_review_show_fields":
      case "visiting_employee_toggle":
      case "visitor_auto_suggest":
      case "visitor_remember_me":
      case "visit_duration_notification":
      case "visit_duration_host_notification":
      case "visit_out_notification":
      case "visit_out_host_notification":
      case "visitor_autologout":
      case "mobile.movements":
      case "mobile.beacon_notifications":
      case "mobile.proximity_verification":
      case "mobile.preregistrations":
      case "contactless.gadget_enabled":
      case "contactless.static_host_selection":
      case "addons.catering.menu[*].enabled":
      case "addons.catering.menu[*].options[*].enabled":
      case "addons.catering.menu[*].options[*].options[*].enabled":
      case "addons.catering.menu[*].options[*].options[*].required":
      case "addons.catering.menu[*].options[*].options[*].multiple":
      case "addons.catering.menu[*].options[*].options[*].options[*].enabled":
      case "addons.deliveries.scanLabel":
      case "addons.deliveries.generalDeliveries.enabled":
      case "addons.catering.settings.hostReceivesOrder":
      case "addons.catering.settings.orderContacts[*].isGeneralContact":
      case "teams_host_notifications":
      case "teams_visitor_approval_notifications":
      case "categories_heading_enabled":
      case "mfa_enforcement":
      case "sso_microsoft":
      case "sso_google":
        settings = updateSettingObject(settings, key, value ? 1 : 0);
        break;
      // strings
      default:
        settings = updateSettingObject(settings, key, value as string);
    }
  });

  return settings;
};

const removeArrayUndefinedElements = (
  arr: ValueOf<DeepPartial<CompanySettings>>[]
) => {
  if (!Array.isArray(arr)) return;
  for (let i = 0; i < arr.length; i++) {
    arr[i] ??= {};
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mergeSettings = (companySettings: any, input: any) => {
  if (
    typeof input !== "object" ||
    typeof companySettings !== "object" ||
    input === null ||
    companySettings === null
  ) {
    // Assign primitive
    return input;
  }

  const newSettings = { ...companySettings };

  for (const key in input) {
    if (Array.isArray(input[key]) && Array.isArray(companySettings[key])) {
      // All keys match for each object in the array
      const keysMatch = input[key].every(
        (arrayItem: UnknownSettings, index: number) =>
          Object.keys(companySettings[key][index] ?? {}).every(
            (companySettingKey) =>
              Object.keys(arrayItem).includes(companySettingKey)
          )
      );
      // The value being updated is at the end of the object chain
      const isAtEnd = input[key].every((arrayItem: UnknownSettings) =>
        Object.values(arrayItem).every((value) => {
          // This is here for catering food images, which require a special case
          if (
            typeof value === "object" &&
            value !== null &&
            Object.keys(value).length === 1 &&
            Object.keys(value)[0] === "key"
          ) {
            return true;
          }

          return typeof value !== "object" || value === null;
        })
      );

      // When all keys match and it's the last item in the object chain then overwrite the array
      // alternatively merge company settings into the array
      newSettings[key] =
        isAtEnd && keysMatch
          ? input[key]
          : assign([], companySettings[key], input[key]);
    } else {
      newSettings[key] = mergeSettings(companySettings[key], input[key]);
    }
  }

  return newSettings;
};

const fillCompanySettingsArrays = (
  input: DeepPartial<CompanySettings>,
  companySettings: UnknownSettings
): DeepPartial<CompanySettings> => {
  if (typeof input !== "object" || typeof companySettings !== "object") {
    // Assign primitive
    return input;
  }
  const output: DeepPartial<CompanySettings> = {};
  for (const k in input) {
    const key = k as keyof CompanySettings;
    const inputSetting = input[key];

    // The company setting is an array
    if (Array.isArray(inputSetting)) {
      const companySettingArray = companySettings[key] as unknown as (
        | SettingValue
        | UnknownSettings
      )[];
      removeArrayUndefinedElements(inputSetting);

      // When there is one primitive property in the array's objects, do a simple merge
      if (
        companySettingArray.every(
          (item) =>
            typeof item === "object" &&
            item !== null &&
            Object.keys(item).length === 1 &&
            typeof Object.values(item)[0] !== "object"
        )
      ) {
        (output[key] as UnknownSettings | SettingValue[]) = merge(
          [],
          companySettingArray,
          inputSetting
        );
        continue;
      }

      (output[key] as UnknownSettings | SettingValue[]) = inputSetting.map(
        (item, index) => {
          if (typeof companySettingArray?.[index] === "object") {
            // Assign array with new values merged over top of company settings
            return fillCompanySettingsArrays(
              mergeSettings(cloneDeep(companySettingArray?.[index]), item),
              companySettingArray?.[index] as UnknownSettings
            );
          }
          // Assign the company setting
          return item;
        }
      );

      const beverages = companySettingArray[0] as UnknownSettings;
      const food = companySettingArray?.[1] as UnknownSettings;
      if (
        k === "menu" &&
        beverages.name === "Beverages" &&
        food.name === "Food"
      ) {
        (output[key] as UnknownSettings | SettingValue[]) =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (output[key] as unknown as any[])?.concat(
            companySettingArray.slice(inputSetting.length)
          );
      }
      continue;
    }

    // The company setting is an object -> recursively copy its values
    if (typeof companySettings[key] === "object" && input[key] !== null) {
      (output[key] as UnknownSettings) = fillCompanySettingsArrays(
        input[key] as DeepPartial<CompanySettings>,
        companySettings[key] as UnknownSettings
      );
      continue;
    }

    // The company setting is a primitive -> just assign the value
    (output[key] as UnknownSettings | SettingValue) = input[key];
  }
  return output;
};

type UpdateCompanySettingsRequest = {
  settings: DeepPartial<CompanySettings>;
  options?: { ignoreLocationGroup?: boolean };
};

export const useUpdateCompanySettings = (
  onSuccess?: (
    response: GetCompanyApiResponse,
    { settings }: UpdateCompanySettingsRequest
  ) => void
) => {
  const queryClient = useQueryClient();
  const apiStore = useApiStore();
  const authStore = useAuthStore();
  const { selectedLocationGroupPk } = useLocationSelector();
  const getCompanySettings = useGetCompanySettings();

  return useMutation<
    GetCompanyApiResponse,
    AxiosError<unknown>,
    UpdateCompanySettingsRequest,
    GetCompanyApiResponse
  >(
    async ({ settings, options }) => {
      if (!authStore.authUser) {
        throw new Error("There is no authenticated user");
      }

      const payload: UpdateCompanyApiRequest = {
        settings: {},
        locationGroupPk: selectedLocationGroupPk.value ?? null,
      };

      if (options?.ignoreLocationGroup) {
        delete payload.locationGroupPk;
      }

      const settingsWithArraysFilled = fillCompanySettingsArrays(
        settings,
        unwrapDeepRefs(getCompanySettings.data) ?? {}
      );

      payload.settings = mapFromCompanySettings(settingsWithArraysFilled);

      const response = await axios.put<GetCompanyApiResponse>(
        apiStore.getEndpoint(
          "platform",
          endpoints.getCompany(authStore.authUser.companyPk)
        ),
        payload
      );

      if (onSuccess) {
        onSuccess(response.data, { settings });
      }

      return response.data;
    },
    {
      onSettled: (_response, _error, request) => {
        queryClient.invalidateQueries(
          queryKeys[tags.platform.company].byLocationGroupId(
            request.options?.ignoreLocationGroup
              ? undefined
              : selectedLocationGroupPk.value
          ).queryKey
        );
        queryClient.invalidateQueries(
          queryKeys[tags.platform.visitorCategories]._def
        );
      },
      onError: (_error, _newSettings, context) => {
        queryClient.setQueryData(
          queryKeys[tags.platform.company].byLocationGroupId(
            selectedLocationGroupPk
          ).queryKey,
          context
        );
      },
      onMutate: async ({ settings }) => {
        await queryClient.cancelQueries(
          queryKeys[tags.platform.company].byLocationGroupId(
            selectedLocationGroupPk
          ).queryKey
        );
        const oldCompany = queryClient.getQueryData<GetCompanyApiResponse>(
          queryKeys[tags.platform.company].byLocationGroupId(
            selectedLocationGroupPk
          ).queryKey
        );

        const newCompany = cloneDeep(oldCompany);
        if (!newCompany) return;
        const settingsWithArraysFilled = fillCompanySettingsArrays(
          settings,
          unwrapDeepRefs(getCompanySettings.data) ?? {}
        );
        const updatedSettings = Object.entries(
          mapFromCompanySettings(settingsWithArraysFilled)
        ).reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: value,
          }),
          {}
        );
        newCompany.company = newCompany.company || {};

        newCompany.company.settings = mergeSettings(
          oldCompany?.company?.settings,
          updatedSettings
        );

        queryClient.setQueryData(
          queryKeys[tags.platform.company].byLocationGroupId(
            selectedLocationGroupPk
          ).queryKey,
          newCompany
        );

        return oldCompany;
      },
    }
  );
};

type CreateCompanyRequest = {
  payload: DeepPartial<CreateCompanyPayload>;
  url: string;
};

export const useCreateCompanyMutation = (
  onSuccess?: () => void,
  onError?: (
    data: AxiosError<CreateCompanyError> & { handledGlobally: boolean }
  ) => void
) => {
  return useMutation<
    AxiosResponse<unknown>,
    AxiosError<CreateCompanyError> & { handledGlobally: boolean },
    CreateCompanyRequest
  >(
    ({ payload, url }) => {
      return axios.put(url, payload, {
        withCredentials: false,
      });
    },
    {
      onError: (_error) => {
        if (onError && _error.response) onError(_error);
      },
      onSuccess: () => {
        onSuccess?.();
      },
    }
  );
};
